import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useMemo } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import { decodeJwtPayload } from "../../../../public/utils/jwtRole";
import { createCheque, editCheque, searchUsers, getChequeById } from "../../api/chequesApi";
import {
  validateChequeEntry,
  getMinChequeDate,
  getMaxChequeDate,
  formatDateForInput,
} from "../../utils/chequeCollectionValidation";
import { masterSelectOptions, filterActiveMasterRows } from "../../../memberships/utils/masterLookupUtils";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import {
  GlobalTextField,
  GlobalSelectField,
  GlobalDateField,
  GlobalTextAreaField,
  GlobalButton,
} from "../../../../global/forms/GlobalFormFields";
import "../../styles/CreateChequeEntryForm.css";
import { compressAttachmentIfImage } from "../../../../utils/compressImage";

function mergeEditMasterRows(baseRows, extraRows) {
  const rows = [...(baseRows || [])];
  const seen = new Set(rows.map((row) => Number(row.id)));
  for (const row of extraRows || []) {
    if (row?.id == null) continue;
    const id = Number(row.id);
    if (!seen.has(id)) {
      seen.add(id);
      rows.push(row);
    }
  }
  return rows;
}

function buildEditExtras(masterList, id, row) {
  if (!id || !row?.id) return [];
  const exists = (masterList || []).some((entry) => Number(entry.id) === Number(id));
  return exists ? [] : [row];
}

const CreateChequeEntryForm = forwardRef(({ onCancel, onSuccess, masterData }, ref) => {
  const alertRef = useRef(null);
  const fileInputRef = useRef(null);

  const loggedInUser = useMemo(() => {
    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("jwt_token")
        : null;
    return decodeJwtPayload(token) || {};
  }, []);

  const emptyForm = () => ({
    id: null,
    cheque_type_id: "",
    cheque_number: "",
    cheque_date: "",
    bank_id: "",
    account_number: "",
    amount: "",
    purpose: "",
    payee_name: "",
    voucher_number: "",
    cheque_status_id: "",
    remarks: "",
    clearance_date: "",
    cheque_cleared: false,
    authorized_signatory_id: "",
  });

  const [formData, setFormData] = useState(emptyForm());
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [signatureFile, setSignatureFile] = useState(null);
  const [existingSignatureUrl, setExistingSignatureUrl] = useState("");

  const [signatorySearch, setSignatorySearch] = useState("");
  const [signatoryResults, setSignatoryResults] = useState([]);
  const [showSignatoryResults, setShowSignatoryResults] = useState(false);
  const [signatoryName, setSignatoryName] = useState("");
  const [editMasterRows, setEditMasterRows] = useState({
    banks: [],
    types: [],
    statuses: [],
  });

  const ALERT_ID = "cheque-entry-form-alert";
  const clearanceFieldsEnabled = Boolean(formData.cheque_cleared);

  const typeOptions = useMemo(
    () =>
      masterSelectOptions(
        editing
          ? mergeEditMasterRows(masterData?.types, editMasterRows.types)
          : filterActiveMasterRows(masterData?.types),
        {
          labelKey: "type_name",
          selectedValue: formData.cheque_type_id,
        },
      ),
    [editing, masterData?.types, editMasterRows.types, formData.cheque_type_id],
  );

  const bankOptions = useMemo(
    () =>
      masterSelectOptions(
        editing
          ? mergeEditMasterRows(masterData?.banks, editMasterRows.banks)
          : filterActiveMasterRows(masterData?.banks),
        {
          labelKey: "bank_name",
          selectedValue: formData.bank_id,
        },
      ),
    [editing, masterData?.banks, editMasterRows.banks, formData.bank_id],
  );

  const statusOptions = useMemo(
    () =>
      masterSelectOptions(
        editing
          ? mergeEditMasterRows(masterData?.statuses, editMasterRows.statuses)
          : filterActiveMasterRows(masterData?.statuses),
        {
          labelKey: "status_name",
          selectedValue: formData.cheque_status_id,
        },
      ),
    [editing, masterData?.statuses, editMasterRows.statuses, formData.cheque_status_id],
  );

  const handleChequeNumberChange = (value) => {
    const digits = String(value ?? "").replace(/\D/g, "").slice(0, 15);
    handleFieldChange("cheque_number")(digits);
  };

  const handleFieldChange = (name) => (value) => {
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "bank_id" && masterData?.banks) {
        const bank =
          masterData.banks.find((b) => String(b.id) === String(value)) ||
          editMasterRows.banks.find((b) => String(b.id) === String(value));
        if (bank?.account_number) {
          next.account_number = bank.account_number;
        }
      }
      if (name === "cheque_cleared" && !value) {
        next.cheque_status_id = "";
        next.clearance_date = "";
      }
      return next;
    });
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
  };

  useEffect(() => {
    const delay = setTimeout(async () => {
      if (signatorySearch.length >= 2) {
        try {
          const resp = await searchUsers(signatorySearch);
          setSignatoryResults(resp?.data || []);
          setShowSignatoryResults(true);
        } catch {
          setSignatoryResults([]);
        }
      } else {
        setSignatoryResults([]);
        setShowSignatoryResults(false);
      }
    }, 300);
    return () => clearTimeout(delay);
  }, [signatorySearch]);

  const handleSignatorySelect = (user) => {
    setFormData((prev) => ({
      ...prev,
      authorized_signatory_id: user.id,
    }));
    setSignatoryName(user.name);
    setShowSignatoryResults(false);
    setSignatorySearch("");
  };

  const openEdit = (item) => {
    setEditing(true);
    setEditMasterRows({
      banks: buildEditExtras(masterData?.banks, item.bank_id, {
        id: item.bank_id,
        bank_name: item.bank_name,
        status_id: 2,
      }),
      types: buildEditExtras(masterData?.types, item.cheque_type_id, {
        id: item.cheque_type_id,
        type_name: item.type_name,
        status_id: 2,
      }),
      statuses: buildEditExtras(masterData?.statuses, item.cheque_status_id, {
        id: item.cheque_status_id,
        status_name: item.status_name,
        status_id: 2,
      }),
    });
    setFormData({
      id: item.id,
      cheque_type_id: item.cheque_type_id || "",
      cheque_number: item.cheque_number || item.cheque_no || "",
      cheque_date: formatDateForInput(item.cheque_date),
      bank_id: item.bank_id || "",
      account_number: item.account_number || item.bank_master_account_number || "",
      amount: item.amount || "",
      purpose: item.purpose || "",
      payee_name: item.payee_name || "",
      voucher_number: item.voucher_number || item.voucher_no || "",
      cheque_status_id: item.cheque_status_id || "",
      remarks: item.remarks || "",
      clearance_date: formatDateForInput(item.clearance_date),
      cheque_cleared: Boolean(Number(item.cheque_cleared)),
      authorized_signatory_id: item.authorized_signatory_id || "",
    });
    setSignatoryName(item.authorized_signatory_name || "");
    setExistingSignatureUrl(item.signature_file_url || "");
    setSignatureFile(null);
    setFormError("");
    setFormErrorDetails({});
  };

  const resetForm = () => {
    setEditing(false);
    setEditMasterRows({ banks: [], types: [], statuses: [] });
    setFormData(emptyForm());
    setSignatoryName("");
    setSignatorySearch("");
    setSignatureFile(null);
    setExistingSignatureUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setFormError("");
    setFormErrorDetails({});
  };

  useImperativeHandle(ref, () => ({
    loadAndEdit: async (chequeId) => {
      setLoading(true);
      try {
        const responseData = await getChequeById(chequeId);
        let cheque = responseData?.data || responseData?.cheque || responseData;
        if (Array.isArray(cheque)) cheque = cheque[0];
        openEdit(cheque);
      } catch (err) {
        setFormError(`Error fetching cheque details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    },
    resetForm,
  }));

  const handleSignatureChange = async (ev) => {
    const file = ev.target.files?.[0] || null;
    if (!file) {
      setSignatureFile(null);
      return;
    }
    try {
      const uploadFile = await compressAttachmentIfImage(file);
      setSignatureFile(uploadFile);
      setFormErrorDetails((prev) =>
        prev.signature_file ? { ...prev, signature_file: "" } : prev,
      );
    } catch {
      ev.target.value = "";
      setSignatureFile(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormErrorDetails({});

    const validation = validateChequeEntry(formData);
    const signatureErrors = { ...validation.errors };
    if (editing) {
      if (!signatureFile && !existingSignatureUrl) {
        signatureErrors.signature_file = "Signature upload is required";
      }
    } else if (!signatureFile) {
      signatureErrors.signature_file = "Signature upload is required";
    }

    if (!validation.ok || signatureErrors.signature_file) {
      setFormError("Please correct the fields below.");
      setFormErrorDetails(signatureErrors);
      setTimeout(() => alertRef.current?.focus(), 0);
      return;
    }

    setLoading(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      if (editing) {
        await editCheque(formData.id, validation.payload, csrfToken, signatureFile);
        onSuccess?.("Cheque updated successfully");
        resetForm();
      } else {
        const response = await createCheque(
          validation.payload,
          csrfToken,
          signatureFile,
        );
        if (response?.data) {
          onSuccess?.("Cheque saved successfully");
          resetForm();
        } else {
          setFormError(response?.error?.message || "Something went wrong");
          setFormErrorDetails(response?.error?.details || {});
        }
      }
    } catch (err) {
      setFormError(err.message || "Unexpected error occurred");
    }
    setLoading(false);
  };

  const userSearchDropdown = (results, onSelect, visible) =>
    visible && results.length > 0 ? (
      <div className="ochq-user-dropdown">
        {results.map((u) => (
          <button
            key={u.id}
            type="button"
            className="ochq-user-dropdown__item"
            onClick={() => onSelect(u)}
          >
            <span>{u.name}</span>
            <span className="ochq-user-dropdown__meta">
              {u.email || `ID ${u.id}`}
            </span>
          </button>
        ))}
      </div>
    ) : null;

  return (
    <form onSubmit={handleSubmit} noValidate className="ochq-entry-form">
      <GlobalFormShell
        legend={editing ? "Update cheque" : "Cheque issue form"}
        gridClassName="gfs-form-grid--3cols"
        showAlert={!!formError}
        alertText={formError}
        alertId={ALERT_ID}
        formAlertRef={alertRef}
        actions={
          <>
            {editing && (
              <GlobalButton variant="ghost" onClick={handleCancel} disabled={loading}>
                Cancel
              </GlobalButton>
            )}
            <GlobalButton type="submit" variant="primary" loading={loading}>
              {editing ? "Update Cheque" : "Save Cheque"}
            </GlobalButton>
          </>
        }
      >
        <p className="ochq-form-note" style={{ gridColumn: "1 / -1" }}>
          Cheque issue code is generated automatically when you save. Issued by is
          set to the signed-in user ({loggedInUser.name || loggedInUser.email || "you"}).
        </p>

        <GlobalSelectField
          id="cq_type"
          label="Cheque type"
          value={formData.cheque_type_id}
          onChange={handleFieldChange("cheque_type_id")}
          options={typeOptions}
          error={formErrorDetails.cheque_type_id}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalSelectField
          id="cq_bank"
          label="Bank name"
          value={formData.bank_id}
          onChange={handleFieldChange("bank_id")}
          options={bankOptions}
          error={formErrorDetails.bank_id}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextField
          id="cq_account"
          label="Account number"
          value={formData.account_number}
          disabled
          error={formErrorDetails.account_number}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextField
          id="cq_no"
          label="Cheque number"
          value={formData.cheque_number}
          onChange={handleChequeNumberChange}
          inputMode="numeric"
          maxLength={15}
          error={formErrorDetails.cheque_no}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalDateField
          id="cq_date"
          label="Cheque date"
          value={formData.cheque_date}
          onChange={handleFieldChange("cheque_date")}
          min={editing ? undefined : getMinChequeDate()}
          max={editing ? undefined : getMaxChequeDate()}
          error={formErrorDetails.cheque_date}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextField
          id="cq_payee"
          label="Payee name"
          value={formData.payee_name}
          onChange={handleFieldChange("payee_name")}
          error={formErrorDetails.payee_name}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextField
          id="cq_amount"
          label="Amount (₹)"
          type="number"
          min={1}
          step="0.01"
          value={formData.amount}
          onChange={handleFieldChange("amount")}
          error={formErrorDetails.amount}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextField
          id="cq_voucher"
          label="Voucher number"
          value={formData.voucher_number}
          onChange={handleFieldChange("voucher_number")}
          error={formErrorDetails.voucher_no}
          baseAlertId={ALERT_ID}
          required
        />

        <GlobalTextAreaField
          id="cq_purpose"
          label="Purpose"
          value={formData.purpose}
          onChange={handleFieldChange("purpose")}
          error={formErrorDetails.purpose}
          baseAlertId={ALERT_ID}
          rows={2}
          classNames={{ fieldWrap: "gff-field gff-field--full" }}
          required
        />

        <div className="ochq-issuer-row">
          <GlobalTextField
            id="cq_issued_by"
            label="Issued by"
            value={loggedInUser.name || loggedInUser.email || "Current user"}
            disabled
          />

          <div className="gff-field ochq-signatory-field">
            <label className="gff-label" htmlFor="cq_signatory_search">
              Authorized signatory <span aria-hidden="true">*</span>
            </label>
            {formData.authorized_signatory_id ? (
              <div className="ochq-selected-user">
                <span>{signatoryName}</span>
                <GlobalButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFormData((p) => ({ ...p, authorized_signatory_id: "" }));
                    setSignatoryName("");
                  }}
                >
                  Change
                </GlobalButton>
              </div>
            ) : (
              <>
                <GlobalTextField
                  id="cq_signatory_search"
                  placeholder="Search name, email, or ID…"
                  value={signatorySearch}
                  onChange={setSignatorySearch}
                  error={formErrorDetails.authorized_signatory_id}
                  baseAlertId={ALERT_ID}
                />
                {userSearchDropdown(
                  signatoryResults,
                  handleSignatorySelect,
                  showSignatoryResults,
                )}
              </>
            )}
          </div>

          <div className="gff-field ochq-file-upload">
            <span
              className={`gff-label${formErrorDetails.signature_file ? " gff-label--error" : ""}`}
            >
              Signature upload <span aria-hidden="true">*</span>
            </span>
            <div className="ochq-file-upload__row">
              <input
                ref={fileInputRef}
                id="cq_signature"
                type="file"
                className="ochq-file-upload__native"
                accept="image/png,image/jpeg,image/webp,application/pdf"
                onChange={handleSignatureChange}
                required={!editing || !existingSignatureUrl}
                aria-invalid={formErrorDetails.signature_file ? "true" : "false"}
                aria-describedby={
                  formErrorDetails.signature_file ? "cq-signature-error" : undefined
                }
              />
              <GlobalButton
                type="button"
                variant="primary"
                size="sm"
                disabled={loading}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </GlobalButton>
              {signatureFile ? (
                <>
                  <span className="ochq-file-upload__filename" title={signatureFile.name}>
                    {signatureFile.name}
                  </span>
                  <GlobalButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={loading}
                    onClick={() => {
                      setSignatureFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    Remove
                  </GlobalButton>
                </>
              ) : existingSignatureUrl ? (
                <a
                  className="ochq-file-upload__link"
                  href={existingSignatureUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  View current signature
                </a>
              ) : (
                <span className="ochq-file-upload__hint">PNG, JPG, or PDF</span>
              )}
            </div>
            {formErrorDetails.signature_file ? (
              <p id="cq-signature-error" className="gff-error" role="alert">
                {formErrorDetails.signature_file}
              </p>
            ) : null}
          </div>
        </div>

        <GlobalTextAreaField
          id="cq_remarks"
          label="Remarks"
          value={formData.remarks}
          onChange={handleFieldChange("remarks")}
          error={formErrorDetails.remarks}
          baseAlertId={ALERT_ID}
          rows={2}
          classNames={{ fieldWrap: "gff-field gff-field--full" }}
        />

        <div className="ochq-clearance-card">
          <div className="ochq-clearance-card__header">
            <label className="ochq-clearance-card__toggle">
              <input
                type="checkbox"
                checked={Boolean(formData.cheque_cleared)}
                onChange={(ev) =>
                  handleFieldChange("cheque_cleared")(ev.target.checked)
                }
              />
              <span className="ochq-clearance-card__title">Cheque cleared</span>
            </label>
            <p className="ochq-clearance-card__hint">
              Turn on when this cheque has cleared to record status and clearance date.
            </p>
          </div>

          {clearanceFieldsEnabled ? (
            <div className="ochq-clearance-card__fields">
              <GlobalSelectField
                id="cq_status"
                label="Cheque status"
                value={formData.cheque_status_id}
                onChange={handleFieldChange("cheque_status_id")}
                options={statusOptions}
                error={formErrorDetails.cheque_status_id}
                baseAlertId={ALERT_ID}
                placeholderOption="Select status"
                required
              />

              <GlobalDateField
                id="cq_clearance_date"
                label="Clearance date"
                value={formData.clearance_date}
                onChange={handleFieldChange("clearance_date")}
                min={formData.cheque_date || undefined}
                error={formErrorDetails.clearance_date}
                baseAlertId={ALERT_ID}
                required
              />
            </div>
          ) : null}
        </div>
      </GlobalFormShell>
    </form>
  );

  function handleCancel() {
    if (loading) return;
    resetForm();
    onCancel?.();
  }
});

export default CreateChequeEntryForm;
